import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';

const { FiFilter, FiX, FiSearch, FiCalendar, FiDollarSign } = FiIcons;

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filteredVehicles, filters, setFilters } = useInventory();
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Handle URL params for filtering
  React.useEffect(() => {
    const modelParam = searchParams.get('model');
    const typeParam = searchParams.get('type');

    if (modelParam) {
      setFilters(prev => ({ ...prev, model: modelParam }));
    }
    if (typeParam) {
      setFilters(prev => ({ ...prev, condition: typeParam === 'certified' ? 'Certified Pre-Owned' : '' }));
    }
  }, [searchParams, setFilters]);

  const filterOptions = {
    model: ['Q3', 'Q5', 'Q7', 'Q8', 'A4', 'A6', 'e-tron', 'S5 Sportback'],
    condition: ['New', 'Certified Pre-Owned'],
    bodyStyle: ['SUV', 'Sedan', 'Sportback'],
    drivetrain: ['Quattro AWD', 'Quattro Electric AWD'],
    fuelType: ['Gasoline', 'Electric'],
    transmission: ['Automatic'],
    priceRange: [
      { label: 'Under $50k', value: '0-50000' },
      { label: '$50k - $60k', value: '50000-60000' },
      { label: '$60k - $70k', value: '60000-70000' },
      { label: '$70k - $80k', value: '70000-80000' },
      { label: 'Over $80k', value: '80000' }
    ]
  };

  const handleFilterChange = (filterType, value) => {
    setFilters({ ...filters, [filterType]: filters[filterType] === value ? '' : value });
  };

  const clearFilters = () => {
    setFilters({
      model: '',
      year: '',
      priceRange: '',
      type: '',
      transmission: '',
      fuelType: '',
      bodyStyle: '',
      drivetrain: '',
      condition: '',
    });
    setSearchTerm('');
    setSearchParams({});
  };

  const searchedVehicles = filteredVehicles.filter(vehicle => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      vehicle.model.toLowerCase().includes(searchLower) ||
      vehicle.year.toString().includes(searchLower) ||
      vehicle.features.some(feature => feature.toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
            {filters.condition === 'Certified Pre-Owned' ? 'Certified Pre-Owned' : 'New'} Audi Inventory
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Browse our selection of {filters.condition === 'Certified Pre-Owned' ? 'certified pre-owned' : 'new'} Audi vehicles. Use the filters to find your perfect match or contact us for a personalized recommendation.
          </p>
        </motion.div>

        {/* Search and Filter Controls */}
        <div className="bg-white shadow-md p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by model, year, or features..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600"
              />
            </div>
            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-red-600 text-white px-6 py-2 hover:bg-red-700 transition-colors duration-200 flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
            >
              <SafeIcon icon={FiFilter} className="w-5 h-5" />
              <span>Filters</span>
            </button>
            {/* Clear Filters */}
            <button
              onClick={clearFilters}
              className="border border-gray-300 text-gray-700 px-6 py-2 hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
              <span>Clear</span>
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-gray-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Model Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Model</label>
                  <select
                    value={filters.model}
                    onChange={(e) => handleFilterChange('model', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Models</option>
                    {filterOptions.model.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </div>

                {/* Condition Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Condition</label>
                  <select
                    value={filters.condition}
                    onChange={(e) => handleFilterChange('condition', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Conditions</option>
                    {filterOptions.condition.map(condition => (
                      <option key={condition} value={condition}>{condition}</option>
                    ))}
                  </select>
                </div>

                {/* Body Style Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Body Style</label>
                  <select
                    value={filters.bodyStyle}
                    onChange={(e) => handleFilterChange('bodyStyle', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Body Styles</option>
                    {filterOptions.bodyStyle.map(style => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Price Range</label>
                  <select
                    value={filters.priceRange}
                    onChange={(e) => handleFilterChange('priceRange', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Prices</option>
                    {filterOptions.priceRange.map(range => (
                      <option key={range.value} value={range.value}>{range.label}</option>
                    ))}
                  </select>
                </div>

                {/* Drivetrain Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Drivetrain</label>
                  <select
                    value={filters.drivetrain}
                    onChange={(e) => handleFilterChange('drivetrain', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Drivetrains</option>
                    {filterOptions.drivetrain.map(drivetrain => (
                      <option key={drivetrain} value={drivetrain}>{drivetrain}</option>
                    ))}
                  </select>
                </div>

                {/* Fuel Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 uppercase tracking-wider">Fuel Type</label>
                  <select
                    value={filters.fuelType}
                    onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                    className="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">All Fuel Types</option>
                    {filterOptions.fuelType.map(fuel => (
                      <option key={fuel} value={fuel}>{fuel}</option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            Showing {searchedVehicles.length} of {filteredVehicles.length} vehicles
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select className="border border-gray-300 px-3 py-1 focus:outline-none focus:ring-2 focus:ring-red-600 text-sm">
              <option>Price: Low to High</option>
              <option>Price: High to Low</option>
              <option>Newest First</option>
              <option>Model A-Z</option>
            </select>
          </div>
        </div>

        {/* Vehicle List */}
        {searchedVehicles.length > 0 ? (
          <div className="space-y-6">
            {searchedVehicles.map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3">
                    <img
                      src={vehicle.images[0]}
                      alt={`${vehicle.year} Audi ${vehicle.model}`}
                      className="w-full h-60 md:h-full object-cover"
                    />
                    {vehicle.condition === 'Certified Pre-Owned' && (
                      <div className="absolute top-2 left-2 bg-blue-600 text-white px-3 py-1 text-sm font-medium uppercase tracking-wider">
                        Certified Pre-Owned
                      </div>
                    )}
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                      <div>
                        <h3 className="text-2xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                          {vehicle.year} Audi {vehicle.model}
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 text-sm">{vehicle.exterior}</span>
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 text-sm">{vehicle.interior}</span>
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 text-sm">Stock #{vehicle.stock}</span>
                        </div>
                      </div>
                      <div className="text-3xl font-bold text-red-600">
                        ${vehicle.price.toLocaleString()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 my-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Engine</p>
                        <p className="font-medium">{vehicle.engine}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Drivetrain</p>
                        <p className="font-medium">{vehicle.drivetrain}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Fuel Economy</p>
                        <p className="font-medium">{vehicle.mpg}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Mileage</p>
                        <p className="font-medium">{vehicle.mileage.toLocaleString()} km</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {vehicle.features.slice(0, 4).map((feature, i) => (
                        <span key={i} className="bg-gray-100 px-2 py-1 text-xs">
                          {feature}
                        </span>
                      ))}
                      {vehicle.features.length > 4 && (
                        <span className="text-gray-500 text-xs px-2 py-1">
                          +{vehicle.features.length - 4} more
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link
                        to={`/vehicle/${vehicle.id}`}
                        className="bg-red-600 text-white px-6 py-3 font-medium hover:bg-red-700 transition-colors text-center flex-1 uppercase tracking-wider text-sm"
                      >
                        View Details
                      </Link>
                      <Link
                        to={`/contact?vehicle=${vehicle.id}`}
                        className="bg-black text-white px-6 py-3 font-medium hover:bg-gray-900 transition-colors text-center flex-1 flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
                      >
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span>Test Drive</span>
                      </Link>
                      <Link
                        to={`/financing?vehicle=${vehicle.id}`}
                        className="bg-white border border-black text-black px-6 py-3 font-medium hover:bg-gray-50 transition-colors text-center flex-1 flex items-center justify-center space-x-2 uppercase tracking-wider text-sm"
                      >
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                        <span>Financing</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiSearch} className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-light text-gray-700 mb-2 uppercase tracking-tight">No vehicles found</h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters or search terms, or contact us for assistance in finding your perfect Audi.
            </p>
            <button
              onClick={clearFilters}
              className="bg-red-600 text-white px-6 py-2 hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;